import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";
import { createThemePatterns, createColorPatterns, createUnitPatterns } from "./ControlPatternMixins";

const VALID_THEMES = ['battery', 'signal', 'led', 'gradient', 'minimal'] as const;

const THEME_ALIASES: Record<string, string> = {
  'bars': 'signal',
  'wifi': 'signal',
  'dots': 'led',
  'leds': 'led',
};

export class LevelIndicatorCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // Theme patterns
    for (const [regex, handler] of createThemePatterns([...VALID_THEMES], THEME_ALIASES)) {
      this.commandPatterns.set(regex, handler);
    }

    // Color patterns
    for (const [regex, handler] of createColorPatterns(['fillColor', 'emptyColor', 'warningColor', 'criticalColor'])) {
      this.commandPatterns.set(regex, handler);
    }

    // Unit patterns
    for (const [regex, handler] of createUnitPatterns()) {
      this.commandPatterns.set(regex, handler);
    }

    // Segments
    this.commandPatterns.set(
      /(?:set )?segments? (?:to |= ?)?(\d+)/i,
      (match) => ({ properties: { segments: parseInt(match[1]) } })
    );

    // Orientation
    this.commandPatterns.set(
      /(?:make |set )?(?:orientation )?(horizontal|vertical)/i,
      (match) => ({ properties: { orientation: match[1].toLowerCase() } })
    );

    // Warning threshold
    this.commandPatterns.set(
      /(?:set )?warning (?:threshold |at |level )?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { warningThreshold: parseFloat(match[1]) } })
    );

    // Critical threshold
    this.commandPatterns.set(
      /(?:set )?critical (?:threshold |at |level )?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { criticalThreshold: parseFloat(match[1]) } })
    );

    // Show/hide value
    this.commandPatterns.set(
      /show value/i,
      () => ({ properties: { showValue: true } })
    );
    this.commandPatterns.set(
      /hide value/i,
      () => ({ properties: { showValue: false } })
    );

    // Show/hide percentage
    this.commandPatterns.set(
      /show percent(?:age)?/i,
      () => ({ properties: { showPercentage: true } })
    );
    this.commandPatterns.set(
      /hide percent(?:age)?/i,
      () => ({ properties: { showPercentage: false } })
    );

    // Value
    this.commandPatterns.set(
      /(?:set )?value (?:to |= ?)?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { value: parseFloat(match[1]) } })
    );

    // Min/max
    this.commandPatterns.set(
      /(?:set )?min(?:imum)? (?:to |= ?)?(-?\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { min: parseFloat(match[1]) } })
    );
    this.commandPatterns.set(
      /(?:set )?max(?:imum)? (?:to |= ?)?(-?\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { max: parseFloat(match[1]) } })
    );

    // Label
    this.commandPatterns.set(
      /(?:set )?label (?:to )?["']?(.+?)["']?\s*$/i,
      (match) => ({ properties: { label: match[1].trim() } })
    );
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.LEVEL_INDICATOR && this.hasKeywords(command);
  }

  private hasKeywords(command: string): boolean {
    const keywords = [
      "level", "indicator", "battery", "signal", "segments", "threshold",
      "warning", "critical", "fill", "theme", "orientation", "value",
      "min", "max", "label", "unit", "color", "percentage", "percent",
      "battery", "signal", "led", "gradient", "minimal",
      "horizontal", "vertical",
    ];
    const lowerCommand = command.toLowerCase();
    return keywords.some(keyword => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {} };

    for (const part of commandParts) {
      const trimmed = part.trim();
      if (!trimmed) continue;
      const patternMatch = this.matchPatterns(trimmed, component);
      if (patternMatch?.properties) {
        changes.properties = { ...changes.properties, ...patternMatch.properties };
      }
      if (patternMatch?.style) {
        changes.style = { ...changes.style, ...patternMatch.style };
      }
    }

    if (!changes.properties || Object.keys(changes.properties).length === 0) {
      return {
        success: false,
        error: "Could not understand level indicator command",
        suggestions: await this.getExampleCommands(ComponentType.LEVEL_INDICATOR),
      };
    }

    const validation = this.validateChanges(changes, component);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    return {
      success: true,
      command: {
        type: this.determineCommandType(changes),
        changes,
        confidence: this.calculateConfidence(command, changes),
        description: this.generateDescription(changes, component),
      },
    };
  }

  protected override validateChanges(changes: PropertyChange, component: Component): { valid: boolean; error?: string } {
    const props = (component.properties || {}) as Record<string, unknown>;
    const min = (changes.properties?.min as number) ?? (props.min as number) ?? 0;
    const max = (changes.properties?.max as number) ?? (props.max as number) ?? 100;
    const segments = changes.properties?.segments as number | undefined;
    const warningThreshold = (changes.properties?.warningThreshold as number) ?? (props.warningThreshold as number | undefined);
    const criticalThreshold = (changes.properties?.criticalThreshold as number) ?? (props.criticalThreshold as number | undefined);

    if (min >= max) {
      return { valid: false, error: "Min must be less than max" };
    }
    if (segments != null && (segments < 2 || segments > 50)) {
      return { valid: false, error: "Segments must be between 2 and 50" };
    }
    if (warningThreshold != null && criticalThreshold != null && warningThreshold <= criticalThreshold) {
      return { valid: false, error: "Warning threshold must be greater than critical threshold" };
    }

    return super.validateChanges(changes, component);
  }

  async getExampleCommands(_componentType: ComponentType): Promise<string[]> {
    return [
      "Set segments to 10",
      "Warning at 30",
      "Critical at 10",
      "Make horizontal",
      "Theme battery",
      "Fill color green",
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const props = (component.properties || {}) as Record<string, unknown>;
    const currentTheme = (props.theme as string) ?? 'battery';

    suggestions.push("Set segments to 10");
    suggestions.push("Warning at 30");
    suggestions.push("Critical at 10");

    const otherThemes = VALID_THEMES.filter(t => t !== currentTheme);
    if (otherThemes.length > 0) {
      suggestions.push(`Theme ${otherThemes[0]}`);
    }

    suggestions.push("Make horizontal");
    suggestions.push("Fill color green");

    return suggestions.slice(0, 6);
  }
}

export const levelIndicatorCommandHandler = new LevelIndicatorCommandHandler();
